/**
 * WorkspaceActivityIndicator.jsx — RC 1.53 Phase 4.2.1
 *
 * Calm, ambient activity chip surfacing the controller's
 * per-(jobId, actionCode) status without blocking unrelated
 * interactions. Phase 4.2.1 wires CV variant only. Other actions
 * surface via the same component once Phase 4.3 migrates them.
 *
 * Rendered inside CvSection (Phase 4.2.1 scope). Phase 4.3 can lift
 * it into the workspace shell.
 *
 *   <WorkspaceActivityIndicator
 *     jobId={jobId}
 *     actionCode="cv_variant"
 *     progressLabel={progressLabel}   ← section's auxiliary state
 *   />
 *
 * Visible only while status ∈ { dispatching, polling }. Hidden on
 * idle / complete / failed / aborted. Locks NO CTAs (the section's
 * own `generating` boolean still owns the Generate button gate).
 */

import { useOperationLifecycle } from '@/lib/operationController/useOperationLifecycle';
import { getStatusMessage } from '@/lib/operationController/statusMessages';
import { Loader2 } from 'lucide-react';

const _ACTIVE = new Set(['dispatching', 'polling', 'stabilizing']);

export default function WorkspaceActivityIndicator({
  jobId,
  actionCode,
  progressLabel = null,
}) {
  const { status } = useOperationLifecycle(jobId, actionCode);

  if (!_ACTIVE.has(status)) return null;

  const message = getStatusMessage(actionCode, status, progressLabel)
                  || 'Working on it…';

  return (
    <div
      className="inline-flex items-center gap-2 rounded-full border border-ndor-edge bg-ndor-surface-alt/60 px-3 py-1 text-xs text-ndor-ink-secondary"
      data-testid="workspace-activity-indicator"
      data-action-code={actionCode}
      data-status={status}
      role="status"
      aria-live="polite"
    >
      <Loader2 className="h-3 w-3 animate-spin text-ndor-gold" aria-hidden />
      <span data-testid="workspace-activity-indicator-message">{message}</span>
    </div>
  );
}
